import React__default, { useLayoutEffect, useState, useMemo, useContext } from 'react';
import styled, { css, useTheme, ThemeProvider } from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { tokens } from '../../tokens.js';
import { useQueryChildComponents, HappyWebSystemError } from '../base/SystemComponent.js';
import { Button } from '../button/Button.js';
import { ButtonIcon } from '../button/ButtonIcon.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { Heading } from '../heading/Heading.js';
import { __Icon } from '../icon/Icon.js';
import { LoadingSpinnerOverlay } from '../loading/LoadingSpinner.js';
import { Menu, MenuButtonIcon } from '../menu/Menu.js';
import { setPortalLayer } from '../styles/stylePropTypes.js';
import { Tabs } from '../tabs/Tabs.js';
import { Text } from '../text/Text.js';
import { Box } from './Box.js';
import { Inline } from './Inline.js';
import { Stack } from './Stack.js';

const DETAIL_WIDTH_REM = 34;
/**
 * Helper to clone components - prevents functions being passed by reference, which causes overrides of displayName and
 * dataId.
 */ const clone = function(component) {
    return (...args)=>component(...args);
};
const LayoutDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  height: 100%;
  width: 100%;
  background-color: ${(props)=>props.backgroundColor};
  z-index: ${tokens.layer["z-index"].default};
`;
const Layout = ({ children  })=>{
    const [main] = useQueryChildComponents(children, [
        LayoutMain
    ]);
    return React__default.createElement(LayoutDiv, {
        "data-hpy-web-layout": true,
        backgroundColor: main.length > 0 ? COLOR_PALETTE.slateGrey5 : COLOR_PALETTE.white
    }, children);
};
Layout.dataId = "data-hpy-web-layout";
Layout.displayName = "Layout";
const LayoutList = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
  width: 100%;
  position: relative;
`;
LayoutList.dataId = "data-hpy-web-layout-list";
LayoutList.displayName = "LayoutList";
const LayoutListHeader = (props)=>{
    return React__default.createElement(Box, {
        "data-hpy-web-layout-list-header": true,
        padding: [
            3,
            3.5,
            2,
            3.5
        ]
    }, React__default.createElement(Inline, {
        gap: 2,
        alignY: "center",
        width: "fill"
    }, props.children));
};
LayoutListHeader.dataId = "data-hpy-web-layout-list-header";
LayoutListHeader.displayName = "LayoutListHeader";
const LayoutListHeading = (props)=>{
    return React__default.createElement(Inline, {
        "data-hpy-web-layout-list-heading": true,
        gap: 1.5,
        alignY: "center"
    }, props.children);
};
LayoutListHeading.dataId = "data-hpy-web-layout-list-heading";
LayoutListHeading.displayName = "LayoutListHeading";
const StyledInline = styled(Inline)`
  & > * > * {
    max-width: 40.5rem;
  }
`;
const LayoutListActions = (props)=>{
    return React__default.createElement(Box, {
        "data-hpy-web-layout-list-actions": true,
        flexGrow: 1,
        ...props
    }, React__default.createElement(StyledInline, {
        gap: 1,
        wrap: false,
        width: "fill",
        alignY: "center",
        align: "right"
    }, props.children));
};
LayoutListActions.dataId = "data-hpy-web-layout-list-actions";
LayoutListActions.displayName = "LayoutListActions";
const LayoutListContent = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative;
`;
LayoutListContent.dataId = "data-hpy-web-layout-list-content";
LayoutListContent.displayName = "LayoutListContent";
const StyledBox = styled(Box)`
  ${(props)=>props.overlay ? "top: 0; right: 0; position: absolute;" : "position: relative;"}

  overflow: hidden;
  flex-grow: 1;

  height: 100%;
  height: calc(100vh - 3.5rem);
  max-height: 100vh;
  width: 100%;
  width: ${DETAIL_WIDTH_REM}rem;
  min-width: ${DETAIL_WIDTH_REM}rem;

  border-radius: 1.5rem 0 0 1.5rem;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);

  ${setPortalLayer()}
`;
const DetailContext = React__default.createContext(undefined);
const DetailProvider = (props)=>{
    const [loading, setLoading] = useState(false);
    const value = useMemo(()=>({
            loading,
            setLoading
        }), [
        loading
    ]);
    return React__default.createElement(DetailContext.Provider, {
        value: value
    }, props.children);
};
const useDetail = ()=>{
    const context = useContext(DetailContext);
    if (!context) {
        throw new HappyWebSystemError("useDetail", "Missing DetailContext - a DetailContext must be set.");
    }
    return context;
};
const DetailLoadingSpinner = ()=>{
    const detail = useDetail();
    return detail.loading ? React__default.createElement(LoadingSpinnerOverlay, null) : null;
};
const LayoutListDetail = ({ overlay =true , open =false , children , ...props })=>{
    const theme = useTheme();
    return open ? React__default.createElement(StyledBox, {
        overlay: overlay,
        bgColor: "white",
        "data-hpy-web-layout-detail": true,
        ...setTestId("LayoutDetail"),
        ...props
    }, React__default.createElement(DetailProvider, null, React__default.createElement(ThemeProvider, {
        theme: {
            ...theme,
            PORTAL_LAYER: theme?.PORTAL_LAYER ? theme?.PORTAL_LAYER + 1 : 1
        }
    }, children), React__default.createElement(DetailLoadingSpinner, null))) : null;
};
LayoutListDetail.dataId = "data-hpy-web-layout-detail";
LayoutListDetail.displayName = "LayoutListDetail";
const LayoutListDetailPane = ({ loading =false , children , ...props })=>{
    const [header, nonHeaders] = useQueryChildComponents(children, [
        LayoutListDetailHeader,
        LayoutMainDetailHeader
    ]);
    const [footer, nonFooters] = useQueryChildComponents(nonHeaders, [
        LayoutListDetailFooter,
        LayoutMainDetailFooter
    ]);
    const [content, others] = useQueryChildComponents(nonFooters, [
        LayoutListDetailContent,
        LayoutMainDetailContent
    ]);
    const detail = useDetail();
    useLayoutEffect(()=>{
        detail.setLoading(loading);
    }, [
        detail,
        loading
    ]);
    return React__default.createElement(Stack, {
        "data-hpy-web-layout-detail-pane": true,
        height: "fill",
        ...props
    }, header, content, others, footer);
};
LayoutListDetailPane.dataId = "data-hpy-web-layout-detail-pane";
LayoutListDetailPane.displayName = "LayoutListDetailPane";
const LayoutListDetailActions = ({ children  })=>{
    return React__default.createElement(React__default.Fragment, null, children);
};
LayoutListDetailActions.dataId = "data-hpy-web-layout-actions";
LayoutListDetailActions.displayName = "LayoutListDetailActions";
const LayoutListDetailHeading = ({ children , ...props })=>{
    return React__default.createElement(React__default.Fragment, null, typeof children === "string" ? React__default.createElement(Heading, {
        "data-hpy-web-layout-heading": true,
        ...props,
        level: "3",
        align: "left"
    }, children) : children);
};
LayoutListDetailHeading.dataId = "data-hpy-web-layout-heading";
LayoutListDetailHeading.displayName = "LayoutListDetailHeading";
const InfoStack = styled(Stack)`
  & [${Tabs.dataId}] {
    /* offset the usual padding to allow Tabs to 'sit' on divider. */
    margin-bottom: -1.5rem;
    border-bottom: none;
  }
`;
const LayoutListDetailHeadingInfo = ({ children , ...props })=>{
    return React__default.createElement(InfoStack, {
        "data-hpy-web-layout-heading-info": true,
        ...props
    }, children);
};
LayoutListDetailHeadingInfo.dataId = "data-hpy-web-layout-heading-info";
LayoutListDetailHeadingInfo.displayName = "LayoutListDetailHeadingInfo";
const Divider = styled.div`
  border-top: solid 1px ${COLOR_PALETTE.slateGrey10};
  margin: 0 3rem;
  && {
    margin-top: 0;
  }
`;
const LayoutListDetailHeader = ({ children , ...props })=>{
    const [actions] = useQueryChildComponents(children, [
        LayoutListDetailActions,
        LayoutMainDetailActions
    ]);
    const [heading] = useQueryChildComponents(children, [
        LayoutListDetailHeading,
        LayoutMainDetailHeading
    ]);
    const [info] = useQueryChildComponents(children, [
        LayoutListDetailHeadingInfo,
        LayoutMainDetailHeadingInfo
    ]);
    const left = [];
    const right = [];
    for (const c of React__default.Children.toArray(actions)){
        const comp = c;
        if (comp.props.position === "left") {
            left.push(comp);
        } else {
            right.push(comp);
        }
    }
    return React__default.createElement(Stack, {
        "data-hpy-web-layout-header": true,
        ...props
    }, React__default.createElement(Box, {
        padding: [
            1,
            1,
            0,
            0
        ],
        minHeight: 3.5
    }, React__default.createElement(Inline, {
        gap: 0.5,
        alignY: "center",
        width: "fill"
    }, React__default.createElement(Box, {
        flexGrow: 1,
        padding: [
            0,
            0,
            0,
            3
        ]
    }, React__default.createElement(Inline, {
        gap: 0.5,
        align: "left",
        alignY: "center"
    }, left)), React__default.createElement(Inline, {
        gap: 0.5,
        align: "right",
        alignY: "center"
    }, right))), React__default.createElement(Stack, null, React__default.createElement(Box, {
        padding: [
            0,
            3,
            1.5
        ],
        width: "fill"
    }, React__default.createElement(Stack, {
        gap: 2
    }, heading, info)), info.length > 0 && React__default.createElement(Divider, null)));
};
LayoutListDetailHeader.dataId = "data-hpy-web-layout-header";
LayoutListDetailHeader.displayName = "LayoutListDetailHeader";
const FadeBox = styled.div`
  position: absolute;
  bottom: 0;

  height: 4rem;
  /* prevent gradient from overlaying on right-hand-side scrollbar */
  width: calc(100% - 3rem);

  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, ${COLOR_PALETTE.white} 100%);
  pointer-events: none;
`;
const ContentBox = styled(Box)`
  position: relative;

  ${(props)=>props.$scrollHack && css`
      @supports (-webkit-hyphens: none) {
        & [${Stack.dataId}] {
          /* Prevent existing safari height hacks from being applied. Flex + the multiple scroll elements within the */
          /* detail don't always layout vertically as would be assumed. */
          /* This is less than ideal as the style coupling between Stack and this component shouldn't exist, but */
          /* cannot remove this until the Stack safari height hack is removed. */
          height: initial;
        }
      }
    `}
`;
const LayoutListDetailContent = ({ overflow ="scroll" , children , ...props })=>{
    // TODO: only show fade when scroll is not top aligned in container
    return React__default.createElement(ContentBox, {
        "data-hpy-web-layout-content": true,
        ...props,
        width: "fill",
        flexGrow: 1,
        overflow: "hidden",
        "$scrollHack": overflow !== "scroll"
    }, React__default.createElement(Box, {
        padding: [
            0,
            3
        ],
        width: "fill",
        height: "fill",
        overflow: overflow === "scroll" ? "scroll" : undefined
    }, React__default.createElement(Stack, null, children, overflow === "scroll" && React__default.createElement(Box, {
        padding: [
            1,
            0
        ]
    }))), overflow === "scroll" && React__default.createElement(FadeBox, null));
};
LayoutListDetailContent.dataId = "data-hpy-web-layout-content";
LayoutListDetailContent.displayName = "LayoutListDetailContent";
const StyledFooterBox = styled(Box)`
  justify-content: space-between;
`;
const LayoutListDetailFooter = ({ children , ...props })=>{
    return React__default.createElement(StyledFooterBox, {
        "data-hpy-web-layout-footer": true,
        ...props,
        padding: [
            0,
            3,
            2.5
        ]
    }, children);
};
LayoutListDetailFooter.dataId = "data-hpy-web-layout-footer";
LayoutListDetailFooter.displayName = "LayoutListDetailFooter";
const LayoutListDetailCloseButton = ({ testId ="X" , ...props })=>{
    return React__default.createElement(ButtonIcon, {
        "data-hpy-web-layout-close-button": true,
        icon: "close",
        ...props,
        testId: "LayoutListDetailClose-" + testId
    });
};
LayoutListDetailCloseButton.dataId = "data-hpy-web-layout-close-button";
LayoutListDetailCloseButton.displayName = "LayoutListDetailCloseButton";
const LayoutDetailWideAction = ({ icon , children , ...props })=>{
    const [button, title] = useQueryChildComponents(children, [
        Button
    ]);
    return React__default.createElement(Box, {
        "data-hpy-web-layout-wide-action": true,
        border: "solid",
        borderRadius: "medium",
        borderColor: "slateGrey10",
        padding: [
            0.75,
            0.75,
            0.75,
            1.25
        ],
        ...props
    }, React__default.createElement(Inline, {
        alignY: "center",
        width: "fill"
    }, React__default.createElement(Box, {
        flexGrow: 1
    }, React__default.createElement(Inline, {
        align: "left",
        alignY: "center",
        gap: 1
    }, React__default.createElement(__Icon, {
        icon: icon,
        size: "small"
    }), React__default.createElement(Text, {
        weight: "bold",
        size: "large"
    }, title))), React__default.createElement(Inline, {
        align: "right",
        alignY: "center",
        gap: 1
    }, button)));
};
LayoutDetailWideAction.dataId = "data-hpy-web-layout-wide-action";
LayoutDetailWideAction.displayName = "LayoutDetailWideAction";
const LayoutListDetailActionMenu = ({ testId ="X" , children , ...props })=>{
    return React__default.createElement(Menu, {
        "data-hpy-web-layout-action-menu": true,
        ...props,
        testId: "LayoutListDetailActionMenu-" + testId
    }, React__default.createElement(MenuButtonIcon, {
        icon: "overflow",
        testId: testId
    }), children);
};
LayoutListDetailActionMenu.dataId = "data-hpy-web-layout-action-menu";
LayoutListDetailActionMenu.displayName = "LayoutListDetailActionMenu";
const LayoutFull = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
  width: 100%;
  position: relative;
  ${(props)=>props.condensed && `max-width: ${DETAIL_WIDTH_REM}rem;`}
`;
LayoutFull.dataId = "data-hpy-web-layout-full";
LayoutFull.displayName = "LayoutFull";
const LayoutFullHeader = clone(LayoutListHeader);
LayoutFullHeader.dataId = "data-hpy-web-layout-full-header";
LayoutFullHeader.displayName = "LayoutFullHeader";
const LayoutFullHeading = clone(LayoutListHeading);
LayoutFullHeading.dataId = "data-hpy-web-layout-full-heading";
LayoutFullHeading.displayName = "LayoutFullHeading";
const LayoutFullActions = clone(LayoutListActions);
LayoutFullActions.dataId = "data-hpy-web-layout-full-actions";
LayoutFullActions.displayName = "LayoutFullActions";
const LayoutFullContent = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;
LayoutFullContent.dataId = "data-hpy-web-layout-full-content";
LayoutFullContent.displayName = "LayoutFullContent";
const LayoutMain = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
  width: 100%;
  position: relative;
`;
LayoutMain.dataId = "data-hpy-web-layout-main";
LayoutMain.displayName = "LayoutMain";
const LayoutMainHeader = clone(LayoutListHeader);
LayoutMainHeader.dataId = "data-hpy-web-layout-main-header";
LayoutMainHeader.displayName = "LayoutMainHeader";
const LayoutMainLeftActions = (props)=>{
    return React__default.createElement(Box, {
        "data-hpy-web-layout-main-left-actions": true,
        ...props,
        flexGrow: 1
    }, React__default.createElement(StyledInline, {
        gap: 1,
        wrap: false,
        width: "fill",
        alignY: "center",
        align: "left"
    }, props.children));
};
LayoutMainLeftActions.dataId = "data-hpy-web-layout-main-left-actions";
LayoutMainLeftActions.displayName = "LayoutMainLeftActions";
const LayoutMainRightActions = (props)=>{
    return React__default.createElement(Box, {
        "data-hpy-web-layout-main-right-actions": true,
        ...props,
        flexGrow: 1
    }, React__default.createElement(StyledInline, {
        gap: 1,
        wrap: false,
        width: "fill",
        alignY: "center",
        align: "right"
    }, props.children));
};
LayoutMainRightActions.dataId = "data-hpy-web-layout-main-right-actions";
LayoutMainRightActions.displayName = "LayoutMainRightActions";
const LayoutMainContent = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;
LayoutMainContent.dataId = "data-hpy-web-layout-main-content";
LayoutMainContent.displayName = "LayoutMainContent";
const cloneAsMainDetail = function(comp) {
    return (props, ...args)=>comp({
            ...props,
            overlay: false
        }, ...args);
};
const LayoutMainDetail = cloneAsMainDetail(LayoutListDetail);
LayoutMainDetail.dataId = "data-hpy-web-layout-main-detail";
LayoutMainDetail.displayName = "LayoutMainDetail";
const LayoutMainDetailPane = clone(LayoutListDetailPane);
LayoutMainDetailPane.dataId = "data-hpy-web-layout-main-detail-pane";
LayoutMainDetailPane.displayName = "LayoutMainDetailPane";
const LayoutMainDetailActions = clone(LayoutListDetailActions);
LayoutMainDetailActions.dataId = "data-hpy-web-layout-main-actions";
LayoutMainDetailActions.displayName = "LayoutMainDetailActions";
const LayoutMainDetailHeading = clone(LayoutListDetailHeading);
LayoutMainDetailHeading.dataId = "data-hpy-web-layout-main-heading";
LayoutMainDetailHeading.displayName = "LayoutMainDetailHeading";
const LayoutMainDetailHeadingInfo = clone(LayoutListDetailHeadingInfo);
LayoutMainDetailHeadingInfo.dataId = "data-hpy-web-layout-main-heading-info";
LayoutMainDetailHeadingInfo.displayName = "LayoutMainDetailHeadingInfo";
const LayoutMainDetailHeader = clone(LayoutListDetailHeader);
LayoutMainDetailHeader.dataId = "data-hpy-web-layout-main-header";
LayoutMainDetailHeader.displayName = "LayoutMainDetailHeader";
const LayoutMainDetailContent = clone(LayoutListDetailContent);
LayoutMainDetailContent.dataId = "data-hpy-web-layout-main-content";
LayoutMainDetailContent.displayName = "LayoutMainDetailContent";
const LayoutMainDetailFooter = clone(LayoutListDetailFooter);
LayoutMainDetailFooter.dataId = "data-hpy-web-layout-main-footer";
LayoutMainDetailFooter.displayName = "LayoutMainDetailFooter";
const LayoutMainDetailCloseButton = clone(LayoutListDetailCloseButton);
LayoutMainDetailCloseButton.dataId = "data-hpy-web-layout-main-close-button";
LayoutMainDetailCloseButton.displayName = "LayoutMainDetailCloseButton";
const LayoutMainDetailActionMenu = clone(LayoutListDetailActionMenu);
LayoutMainDetailActionMenu.dataId = "data-hpy-web-layout-main-action-menu";
LayoutMainDetailActionMenu.displayName = "LayoutMainDetailActionMenu";

export { Layout, LayoutDetailWideAction, LayoutFull, LayoutFullActions, LayoutFullContent, LayoutFullHeader, LayoutFullHeading, LayoutList, LayoutListActions, LayoutListContent, LayoutListDetail, LayoutListDetailActionMenu, LayoutListDetailActions, LayoutListDetailCloseButton, LayoutListDetailContent, LayoutListDetailFooter, LayoutListDetailHeader, LayoutListDetailHeading, LayoutListDetailHeadingInfo, LayoutListDetailPane, LayoutListHeader, LayoutListHeading, LayoutMain, LayoutMainContent, LayoutMainDetail, LayoutMainDetailActionMenu, LayoutMainDetailActions, LayoutMainDetailCloseButton, LayoutMainDetailContent, LayoutMainDetailFooter, LayoutMainDetailHeader, LayoutMainDetailHeading, LayoutMainDetailHeadingInfo, LayoutMainDetailPane, LayoutMainHeader, LayoutMainLeftActions, LayoutMainRightActions };
